'use client';

import {
    Select,
    SelectContent,
    SelectTrigger,
} from '@app/_components/ui/Select/Select';
import { useState } from 'react';
import type { Breadcrumb, Dictionary } from 'types';
import type { InitialVariantState } from 'types/variant';

import type { MarketConfiguration } from '@/config/markets/const';
import { cn } from '@/lib/utils';
import useVariantSelector from '@/utils/hooks/useVariantSelector';

import ProductPageVariantSelectButton from './ProductPageVariantSelectButton';
import ProductPageVariantSelectItem from './ProductPageVariantSelectItem';

export type ProductPageVariantSelectProps = {
    marketConfig: MarketConfiguration;
    initialVariantState: InitialVariantState;
    dictionary: Dictionary;
    className?: string;
    breadcrumbs?: Breadcrumb[];
};

export default function ProductPageVariantSelect({
    marketConfig,
    initialVariantState,
    dictionary,
    className,
    breadcrumbs,
}: ProductPageVariantSelectProps) {
    const [open, setOpen] = useState(false);
    const { attributes, state, optionSortingToUse, onChange } =
        useVariantSelector({
            initialVariantState,
            breadcrumbs,
            dictionary,
        });

    return (
        <div
            className={cn('flex flex-col gap-2', className)}
            data-testid="ProductVariantSelect">
            {attributes.map(({ key, Label: label }) => {
                const options = state.options.get(key);

                if (!options) {
                    return null;
                }

                const sortedOptions = [...options].sort(optionSortingToUse);

                return (
                    <div
                        key={key}
                        data-testid="ProductVariantList"
                        className="flex flex-col gap-2">
                        <p className="text-xs">{label}</p>
                        <Select
                            onValueChange={(value) => {
                                onChange(key, value, options);
                            }}
                            onOpenChange={(isOpen) => setOpen(isOpen)}
                            defaultValue={
                                typeof state.selection[key] === 'string'
                                    ? state.selection[key]
                                    : undefined
                            }
                            disabled={sortedOptions.length === 0}>
                            <SelectTrigger
                                name={key}
                                className="h-11 pl-0 lg:h-12"
                                open={open}>
                                <ProductPageVariantSelectButton
                                    selection={state.selection[key]}
                                    label={label}
                                    options={options}
                                    dictionary={dictionary}
                                />
                            </SelectTrigger>
                            <SelectContent className="p-0">
                                {sortedOptions.map(
                                    ([value, variantAttributes]) => (
                                        <ProductPageVariantSelectItem
                                            key={value}
                                            value={value}
                                            variantAttributes={
                                                variantAttributes
                                            }
                                            dictionary={dictionary}
                                            marketConfig={marketConfig}
                                        />
                                    ),
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                );
            })}
        </div>
    );
}
